import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from "@angular/router";
import { ConnectionStatusEnum } from '@models/integrations/connection-status.enum';
import { ExternalSourceType } from "@models/integrations/external-source-type.enum";
import { IntegrationConnectionsEnum } from "@models/integrations/integration-connections.enum";
import { SyncIdentitiesModel } from "@models/integrations/sync-identities-model";
import { SyncStatusEnum } from "@models/integrations/sync-status.enum";
import {
  SyncIdentitiesTypesModalComponent
} from "@pages/settings/components/integrations-page/modals/sync-identities-types-modal/sync-identities-types-modal.component";
import { EntityIdentityApiService } from "@shared/services/entity-identity-api.service";
import { HeaderService, IPartialHeaderData } from "@shared/services/header.service";
import { finalize, firstValueFrom, from, map, tap } from "rxjs";
import { BaseSignProvidersComponent } from "./base-sign-providers/base-sign-providers.component";
import { ConnectionCardComponent } from "./connection-card/connection-card.component";
import { getXpmConnectionItem } from "./constants/connection-items";
import { IConnection, IConnectionGroup } from './integration.view.model';

@Component({
  selector: 'app-integrations-page',
  standalone: true,
  imports: [
    ConnectionCardComponent
  ],
  templateUrl: 'integrations-page.component.html',
  styleUrls: [
    'integrations-page.component.scss',
    './../../styles/settings-styles.scss'
  ]
})
export class IntegrationsPageComponent extends BaseSignProvidersComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private entityIdentityService = inject(EntityIdentityApiService);
  private headerService = inject(HeaderService);

  ConnectionStatusEnum = ConnectionStatusEnum;

  readonly headerData: IPartialHeaderData = {
    title: 'Integrations',
    iconClass: 'ic-link-20',
    backPath: ['/settings']
  };

  loadingBtn = false;
  connectionGroups: IConnectionGroup[] = [
    {
      title: 'Practice Management',
      connections: [
        getXpmConnectionItem([
          {
            displayStatus: [ConnectionStatusEnum.NotConnected],
            loadingBtn: false,
            disabled: false,
            btnTitle: 'Connect',
            action: (connection: IConnection) => from(this.connect(connection))
          },
          {
            displayStatus: [ConnectionStatusEnum.Connected, ConnectionStatusEnum.ConnectionFailed],
            loadingBtn: false,
            disabled: false,
            btnTitle: 'Disconnect',
            action: (connection: IConnection) => this.disconnect(connection)
          },
          {
            displayStatus: [ConnectionStatusEnum.Connected],
            loadingBtn: false,
            disabled: false,
            btnTitle: 'Sync',
            action: (connection: IConnection) => this.sync(connection, ExternalSourceType.Xpm)
          }
        ]),
      ]
    },
    {
      title: 'eSigning',
      connections: this.eSignProviderConnections
    },
  ];

  async ngOnInit() {
    this.headerService.setHeaderData(this.headerData);
    this.isLoading.set(true);
    await this.processParams();
    this.checkConnections();
    this.isLoading.set(false);
  }

  async processParams() {
    const params = await firstValueFrom(this.route.queryParamMap.pipe(
      map((params: ParamMap) => {
        return {
          success: params.get('success'),
          error: params.get('error'),
          state: params.get('state'),
          code: params.get('code'),
          scope: params.get('scope')
        };
      }),
    ));

    if (params.success) {
      this.toastr.success("Connected", "Success");
    }

    if (params.error) {
      this.toastr.error("Failed to connect", "Error");
    }

    if (params.state && params.code && params.scope) {
      await this.processCallback(params.state, params.code);
    }

    window.history.replaceState({}, '', `/settings/integrations`);
  }

  async processCallback(state: string, code: string) {
    try {
      const callBackResult = await firstValueFrom(this.integrationsService.processCallback(IntegrationConnectionsEnum.XpmConnection, state, code));
      if (callBackResult)
        this.toastr.success("Connected", "Success");
    } catch (e) {
      this.toastr.error("Failed to connect", "Error");
    }
  }

  override async connect(connection: IConnection) {
    switch (connection.connectionType) {
      case IntegrationConnectionsEnum.XpmConnection:
        await this.connectToXPM();
        break;
      default:
        await super.connect(connection);
    }
  }

  sync(connection: IConnection, type: ExternalSourceType) {
    connection.syncStatus = SyncStatusEnum.Syncing;
    this.setbtnLoading(connection, 'Sync', true);
    const modalRef = this.modalService.open(SyncIdentitiesTypesModalComponent, { size: 'sm', centered: true });
    (modalRef.componentInstance as SyncIdentitiesTypesModalComponent).sourceType = type;

    (modalRef.componentInstance as SyncIdentitiesTypesModalComponent).confirm
      .subscribe((syncIdentitiesModel: SyncIdentitiesModel) => {
        this.entityIdentityService.sync(type, syncIdentitiesModel)
          .pipe(
            finalize(() => this.setbtnLoading(connection, 'Sync', false)),
            tap(() => this.checkConnection(connection)),
          )
          .subscribe({
            next: () => this.toastr.info(`The data synchronization with ${ ExternalSourceType[type] } has been completed.`, 'Syncing'),
            error: (error) => {
              this.checkConnection(connection);

              if (this.entityIdentityService.checkSyncError(error)) {
                return;
              }

              this.toastr.error(`Failed to sync ${ ExternalSourceType[type] }`, 'Error');
            }
          });
      });
  }

  checkConnections() {
    this.connectionGroups.forEach(group => {
      group.connections.forEach(connection => {
        this.integrationsService.checkConnection(connection.connectionType)
          .subscribe({
            next: (response) => {
              connection.connectionStatus = response.status;
              connection.lastSync = response.lastSyncDate;
              connection.syncStatus = response.syncStatus;

              if (connection.syncStatus && connection.syncStatus === SyncStatusEnum.Syncing) {
                this.setbtnLoading(connection, 'Sync', true);
              }

              this.showResponseToaster(response);
              connection.isLoading = false;
            },
            error: () => {
              connection.connectionStatus = ConnectionStatusEnum.NotConnected;
              connection.isLoading = false;
            }
          });
      });
    });
  }

  async connectToXPM() {
    const redirectionUri = await firstValueFrom(this.integrationsService.connect(IntegrationConnectionsEnum.XpmConnection));
    window.location.href = redirectionUri;
  }

  override setConnectionStatus(connectionType: IntegrationConnectionsEnum, status: ConnectionStatusEnum) {
    this.connectionGroups.forEach(group => {
      const connection = group.connections.find(conn =>
        conn.connectionType !== undefined &&
        conn.connectionType === connectionType
      );

      if (connection)
        connection.connectionStatus = status;
    });
  }

  setbtnLoading(connection: IConnection, btnName: string, isLoading: boolean) {
    const btn = connection.buttons?.find(b => b.btnTitle === btnName);
    if (btn) {
      btn.loadingBtn = isLoading;
    }

    connection.buttons?.forEach(b => {
        b.disabled = isLoading;
      }
    );
  }
}
